import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  CartesianGrid,
} from 'recharts';
import { Users, Eye, TrendingUp, Globe, ShieldCheck, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [
  { key: '24h', label: '24 h' },
  { key: '7d', label: '7 jours' },
  { key: '30d', label: '30 jours' },
  { key: '90d', label: '90 jours' },
];

export default function AnalyticsTab() {
  const { token } = useAuth();
  const [range, setRange] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/admin/analytics/summary?range=${range}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [range, token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-[#737373]" data-testid="analytics-loading">
        <Loader2 className="w-6 h-6 mr-2 animate-spin" /> Chargement des statistiques…
      </div>
    );
  }
  if (error) {
    // The backend route may not have been deployed yet on the user's
    // production instance — give them a clear, actionable message.
    const isMissingRoute = /404|not.?found/i.test(String(error));
    return (
      <div
        className="border border-amber-200 bg-amber-50 rounded-xl p-6 text-sm text-[#1A3C34] space-y-2"
        data-testid="analytics-error"
      >
        <p className="font-bold">Les statistiques ne sont pas disponibles</p>
        <p className="text-[#737373]">Détail technique : {error}</p>
        {isMissingRoute && (
          <p className="text-[#737373]">
            Le module Analytics vient d'être ajouté. Redéployez le backend pour activer l'endpoint <code className="font-mono text-xs">/api/admin/analytics/summary</code>.
          </p>
        )}
      </div>
    );
  }

  const { totals = {}, top_pages = [], daily = [], top_referrers = [], retention_days } = data || {};
  const daysCount = daily.length || 1;
  const avgPerDay = Math.round((totals.views || 0) / daysCount);
  const chartData = daily.map(d => ({
    day: format(parseISO(d.day), 'd MMM', { locale: frLocale }),
    views: d.views,
    uniques: d.uniques,
  }));

  return (
    <div className="space-y-6" data-testid="admin-analytics-panel">
      {/* Header + range picker */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#1A3C34]">
            Visiteurs du site
          </h2>
          <p className="text-xs text-[#737373] mt-1 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-green-600" />
            RGPD-friendly · aucune donnée personnelle stockée · rétention {retention_days} jours
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-[#E2E2DF] bg-white overflow-hidden" data-testid="analytics-range-tabs">
          {RANGES.map(r => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              data-testid={`analytics-range-${r.key}`}
              className={`px-3 py-1.5 text-xs font-bold transition-colors ${
                range === r.key
                  ? 'bg-[#1A3C34] text-white'
                  : 'bg-white text-[#1A3C34] hover:bg-[#F0EFEA]'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="analytics-kpis">
        <KPI
          icon={<Eye className="w-5 h-5" />}
          label="Pages vues"
          value={totals.views ?? 0}
          testid="kpi-views"
        />
        <KPI
          icon={<Users className="w-5 h-5" />}
          label="Visiteurs uniques"
          value={totals.uniques ?? 0}
          testid="kpi-uniques"
        />
        <KPI
          icon={<TrendingUp className="w-5 h-5" />}
          label="Moyenne / jour"
          value={avgPerDay}
          testid="kpi-avg"
        />
      </div>

      {/* Daily chart */}
      <div className="bg-white border border-[#E2E2DF] rounded-xl p-5 shadow-sm" data-testid="analytics-daily-chart">
        <h3 className="font-heading text-lg font-bold text-[#1A3C34] mb-3">Trafic quotidien</h3>
        {chartData.length === 0 ? (
          <p className="text-sm text-[#737373]">Aucune visite pour cette période.</p>
        ) : (
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E2DF" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#1A3C34' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#737373' }} />
                <ChartTooltip
                  cursor={{ fill: 'rgba(26,60,52,0.05)' }}
                  contentStyle={{ borderRadius: 8, border: '1px solid #E2E2DF', fontSize: 12 }}
                />
                <Bar dataKey="views" fill="#1A3C34" name="Pages vues" radius={[4, 4, 0, 0]} />
                <Bar dataKey="uniques" fill="#C77D63" name="Visiteurs uniques" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Two-column: Top pages + Top referrers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top pages */}
        <div className="bg-white border border-[#E2E2DF] rounded-xl p-5 shadow-sm" data-testid="analytics-top-pages">
          <h3 className="font-heading text-lg font-bold text-[#1A3C34] mb-3">Pages les plus vues</h3>
          {top_pages.length === 0 ? (
            <p className="text-sm text-[#737373]">Pas encore de données.</p>
          ) : (
            <ul className="divide-y divide-[#E2E2DF]">
              {top_pages.map(p => (
                <li key={p.path} className="flex items-center justify-between py-2">
                  <span className="font-mono text-xs sm:text-sm text-[#1A3C34] truncate max-w-[70%]" title={p.path}>
                    {p.path}
                  </span>
                  <span className="text-xs text-[#737373]">
                    <strong className="text-[#1A3C34] font-bold">{p.views}</strong> vues
                    <span className="ml-2 text-[10px] uppercase tracking-wider">{p.uniques} uniq.</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top referrers */}
        <div className="bg-white border border-[#E2E2DF] rounded-xl p-5 shadow-sm" data-testid="analytics-top-referrers">
          <h3 className="font-heading text-lg font-bold text-[#1A3C34] mb-3 flex items-center gap-2">
            <Globe className="w-4 h-4" /> Provenance
          </h3>
          {top_referrers.length === 0 ? (
            <p className="text-sm text-[#737373]">Trafic direct uniquement pour cette période.</p>
          ) : (
            <ul className="divide-y divide-[#E2E2DF]">
              {top_referrers.map((r, i) => (
                <li key={i} className="flex items-center justify-between py-2">
                  <span className="text-xs sm:text-sm text-[#1A3C34] truncate max-w-[70%]" title={r.referrer}>
                    {shortenReferrer(r.referrer)}
                  </span>
                  <span className="text-xs text-[#737373]">
                    <strong className="text-[#1A3C34] font-bold">{r.count}</strong>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function KPI({ icon, label, value, testid }) {
  return (
    <div
      className="bg-white border border-[#E2E2DF] rounded-xl p-5 shadow-sm"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-[#C77D63] mb-2">{icon}</div>
      <div className="text-3xl font-heading font-bold text-[#1A3C34]">{value.toLocaleString('fr-BE')}</div>
      <div className="text-xs uppercase tracking-wider text-[#737373] mt-1">{label}</div>
    </div>
  );
}

function shortenReferrer(url) {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
